/* eslint-disable */
import React, { CSSProperties } from "react";
import { Button, Card, Col, Row, Space, Statistic, Typography } from "antd";
import ReactJson from "react-json-view";
import { useSelector, useDispatch } from 'react-redux';
import { update as deliveryCostUpdate } from '../../Actions/Cart.action';
import { reloadPayment } from '../../Actions/Checkout.action';
import {
    createSession,
    updateSession,
    getSession,
} from "./api";
import { replaceScriptNode, setupGeneralJSListeners } from "./utils";
import { SIWSessionDTO } from "./types";
import { RootState } from "./store";

const localStorageSessionIDKey = "ingrid-session-id";
const statusComplete = "COMPLETE";
export default function IngridApp() {
    // This is where the Delivery Checkout will get injected
    const widgetWrapperRef = React.useRef<HTMLDivElement>(null);
    // Used to show the shipping price in 'total cost' section
    const [shippingPrice, setShippingPrice] = React.useState<null | number>(null);
    // Used to show the 'cart value' in 'total cost' for the user,
    const [session, setSession] = React.useState<SIWSessionDTO | null>(null);
    const [htmlSnippet, sethtmlSnippet] = React.useState<string | null>('');
    // Used to show the final result after session is completed
    const [sessionResult, setSessionResult] = React.useState<
        SIWSessionDTO["result"] | null
    >(null);
    // Just to lock the buttons while requests are pending
    const [isLoading, setIsLoading] = React.useState(false);
    const dispatch = useDispatch();
    // const sessionResponse = useSelector((state) => state.ingrid);
    const mapCartToAbort = {};
    const firstRender = React.useRef(true);
    const cart = (state: RootState) => state.cart
    let cartObj = useSelector(cart);
    const ingrid = (state: RootState) => state.ingrid
    const ingridObj = useSelector(ingrid);
    const [isReload, setReLoad] = React.useState(ingridObj.showInfo)
    mapCartToAbort[cartObj.articleNumber] = new AbortController();

    React.useEffect(() => {
        if (firstRender.current) {
            firstRender.current = false;
            return;
        }
        handleUpdateSession(session, true);
    }, [ingridObj.showInfo]);

    React.useEffect(() => {
        initDeliveryCheckout();
    }, []);

    const initDeliveryCheckout = async () => {

        // The user might leave the checkout page and come back again after some time
        // In that case, want to restore the previous session instead of creating a new one
        const existingSessionID = window.localStorage.getItem(
            localStorageSessionIDKey
        );
        if (existingSessionID) {
            setIsLoading(true);
            try {
                const getSessionResponse = await getSession(existingSessionID);
                if (getSessionResponse != undefined) {
                    //if status is completed remove localstorage item
                    if (!getSessionResponse.data || getSessionResponse.data.session.status.toUpperCase() == statusComplete) {
                        throw new Error('Ingrid Session is corrupt');
                    }
                    handleIngridInitialize(
                        getSessionResponse?.data.session,
                        getSessionResponse?.data.html_snippet
                    );
                }
                else {
                    throw new Error('Ingrid Session is corrupt');
                }
            }
            catch {
                // If problem with session it is best to clear and generate new session.
                handleClearSession();
                handleCreateSession();
            }
            finally {
                setIsLoading(false);
            }
        }
        else {
            handleCreateSession();
        }
    };

    const handleIngridInitialize = (
        session: SIWSessionDTO,
        HTMLSnippet: string
    ) => {
        setSession(session);
        sethtmlSnippet(htmlSnippet)
        // Injecting the HTMLSnippet into the dom will setup the app via iframes
        // and secure channels of communication between the iframes and the page
        widgetWrapperRef.current!.innerHTML = HTMLSnippet;

        // Most browsers will not automatically run an injected script
        // We have to clone it, to trick the browser into runing it
        replaceScriptNode(document.getElementById("shipwallet-container"));

        // Whenever the user selects a different shipping option,
        // we want to update the shipping price in the 'total cost' section
        window._sw!((api) => {
            api.on("data_changed", (data, meta) => {
                if (meta.price_changed) {
                    setShippingPrice(data.price);
                }
            });
        });

        // The function below will setup listeners that just console log the received values
        // The events include, for example, address changes or courier instructions change
        //setupGeneralJSListeners();

        if (window._sw) {
            window._sw((api) => {
                api.on("door_code_changed", (doorCode) =>
                    console.log("door code changed: ", doorCode)
                );
                api.on("courier_instructions_changed", (courierInstructions) =>
                    console.log("courier instructions changed: ", courierInstructions)
                );

                api.on("data_changed", (data, meta) => {
                    console.log("data changed", { data, meta });
                    handleUpdateSession(session)
                });
            });
        }
    };

    const handleCreateSession = async () => {
        setIsLoading(true);

        try {
            const sessionResponse = await createSession();
            handleIngridInitialize(
                sessionResponse?.data.session,
                sessionResponse?.data.html_snippet
            );
            // Save session ID in case user leaves the checkout without finishing the purchase
            // We will use it on next checkout init to GET session instead of CREATING a new one
            window.localStorage.setItem(
                localStorageSessionIDKey,
                sessionResponse.data.session.id
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleUpdateSession = async (session, isChange = false) => {

        setIsLoading(true);
        try {
            // We need to suspend the widget, so the user cannot change anything while we're updating the session
            window._sw!((api) => api.suspend());

            // We update the session by simply adding another item to the cart and bumping the price
            if (session != null) {
                const updateSessionResponse = await updateSession(session!.id);

                setSession(updateSessionResponse?.data?.session);
                // Resuming the widget will make it interactive again
                // And cause it to sync with our backend, So that whatever changes you made
                // via `session.update`are visible on the widget now
                window._sw!((api) => api.resume());
                if (!isChange) {
                    dispatch(
                        deliveryCostUpdate(cartObj.articleNumber, cartObj.quantity, mapCartToAbort[cartObj.articleNumber])
                    )
                    dispatch(reloadPayment());
                }
            }

        } finally {
            setIsLoading(false);
        }

    };

    const handleClearSession = () => {
        window.localStorage.removeItem(localStorageSessionIDKey);
        setShippingPrice(null);
        widgetWrapperRef.current!.innerHTML = "";
        setSession(null);
        setSessionResult(null);
    };

    return (
        <div
            style={{
                textAlign: "center",
                width: "100%",
                // maxWidth: "610px",      
                // paddingBottom: "30px",
                // paddingTop: "15px",
            }}
        >
            <Space direction="vertical" align="center" size="middle">
                <div
                    ref={widgetWrapperRef}
                    // style={{ width: "100%", maxWidth: "610px" }}
                    style={{ width: "100%" }}
                />


                {sessionResult && (
                    <Card
                        title="Session result"
                        style={cardStyle}
                        headStyle={cardHeaderStyle}
                    >
                        <ReactJson
                            collapsed={1}
                            src={sessionResult}
                            theme="grayscale:inverted"
                            style={{ textAlign: "left" }}
                        />
                    </Card>
                )}
            </Space>
        </div>
    );
}

// const cardStyle = { maxWidth: 610, width: "100%" };
const cardStyle = { width: "100%" };
const cardHeaderStyle: CSSProperties = {
    textAlign: "left",
    fontWeight: 500,
};
